// Canonical import documents for reference data and expectations (spec §2.2–2.4, §2.9,
// chapter 8 I8.1/I8.2/I8.3). Every adapter (an ERP, a spreadsheet, a despatch advice)
// maps its own format onto these; the importers in @trail/reference know nothing about
// where a document came from. Documents are idempotent: importing the same document
// twice leaves the registry unchanged.
//
// Keys. Parties are keyed by an external identifier within a named `source` (the ERP's
// supplier number, say); products by SKU within a goods owner; packaging levels by
// product + level; locations by site + code; expected receipts by source + reference.
import { z } from "zod";
import { hasValidCheckDigit } from "../identity/checkdigit.js";
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
const DecimalLike = z.union([z.string().regex(/^-?\d+([.,]\d+)?$/), z.number()]).transform((v) => (typeof v === "number" ? String(v) : v.replace(",", ".")));
const Uom = z.string().min(1).max(8); // UN/ECE Rec 20: MTR, KGM, EA, ...
const Key = z.string().trim().min(1).max(64);
/** A GTIN as found in the wild: spaces, leading zeros, 8/12/13/14 digits. Normalised to
 *  GTIN-14 on import; a wrong check digit is an error, not a silent accept. */
export function normalizeGtin(raw) {
    const digits = raw.replace(/\D/g, "").replace(/^0+/, "");
    if (digits.length < 8 || digits.length > 14)
        return null;
    const gtin = digits.padStart(14, "0");
    return hasValidCheckDigit(gtin) ? gtin : null;
}
export const PartyRole = z.enum(["supplier", "customer", "carrier", "authority", "self"]);
export const PartyImport = z.object({
    key: Key,
    name: z.string().trim().min(1).max(200),
    shortName: z.string().trim().max(100).optional(),
    roles: z.array(PartyRole).min(1),
    gln: z.string().regex(/^\d{13}$/).optional(),
    country: z.string().regex(/^[A-Z]{2}$/).optional(),
    orgNumber: z.string().trim().max(40).optional(),
    vatNumber: z.string().trim().max(40).optional(),
    attributes: z.record(z.unknown()).default({}),
});
export const PartiesImport = z.object({
    kind: z.literal("parties"),
    /** Name of the identifier space `key` belongs to, e.g. "visma". */
    source: Key,
    parties: z.array(PartyImport).min(1),
});
export const PackagingLevelImport = z.object({
    level: z.enum(["each", "inner", "case", "pallet"]),
    gtin: z.string().trim().optional(),
    /** The level this one contains, and how much of it (count of that level, or base uom for the lowest level). */
    containsLevel: z.enum(["each", "inner", "case"]).optional(),
    containsQty: DecimalLike.optional(),
    pickable: z.boolean().default(true),
    /** What the operator calls one unit of this level ("Kartong", "Hink"); shown instead of the generic "kolli". */
    displayName: z.string().trim().max(40).optional(),
});
export const QuantityFrom = z.object({
    /** Application identifier on the supplier's label that carries the content, e.g. "3110", "30", "3100". */
    ai: z.string().regex(/^\d{2,4}$/),
    uom: Uom,
});
export const SupplierProductImport = z.object({
    /** Supplier party key within the parties `source`. */
    party: Key,
    gtin: z.string().trim().optional(),
    article: z.string().trim().max(80).optional(),
    quantityFrom: QuantityFrom.optional(),
    lotCodeGrammar: z.record(z.unknown()).optional(),
    /** This supplier's labels differ from the profile (e.g. never a best-before date). */
    captureOverrides: z.record(z.string()).optional(),
});
/** Any GTIN that identifies this product on a label — supplier codes over the years, own
 *  codes, several suppliers. The registry maps each to the product; the level is optional. */
export const ProductGtinImport = z.object({
    gtin: z.string().trim(),
    level: z.enum(["each", "inner", "case", "pallet"]).optional(),
});
export const ProductImport = z.object({
    sku: Key,
    name: z.string().trim().min(1).max(200),
    baseUom: Uom,
    variableMeasure: z.boolean().default(false),
    regulatoryClasses: z.array(z.enum(["food", "animal-origin", "food-contact", "additive", "none"])).default([]),
    storageRequirement: z.string().trim().min(1).max(40).optional(),
    /** The register's standard quantity per carton in the base unit (owner 2026-11-03): proposed, never
     *  auto-confirmed, when a batch-handled article's barcode carries no quantity. */
    standardQuantity: DecimalLike.optional(),
    shelfLife: z
        .object({
        bestBefore: z.enum(["required", "optional", "none"]).default("optional"),
        expiry: z.enum(["required", "optional", "none"]).default("optional"),
        productionDate: z.enum(["required", "optional", "none"]).default("optional"),
    })
        .optional(),
    /** Name of a published handling profile. */
    handlingProfile: z.string().trim().min(1).optional(),
    /** Code of the storage bin the product always goes to when it is free (strategy "fixed bin"). */
    fixedLocation: z.string().trim().min(1).max(60).optional(),
    /** Product category — the classification "category"; kept as a column for the views. */
    category: z.string().trim().max(80).optional(),
    /** Classification values per dimension key (the company's registry of dimensions; a key
     *  not registered yet is created on import). Profile rules key on one of these. */
    classifications: z.record(z.string().trim().max(80)).default({}),
    attributes: z.record(z.unknown()).default({}),
    active: z.boolean().default(true),
    packaging: z.array(PackagingLevelImport).default([]),
    suppliers: z.array(SupplierProductImport).default([]),
    gtins: z.array(ProductGtinImport).default([]),
});
export const ProductsImport = z.object({
    kind: z.literal("products"),
    /** Identifier space of supplier party keys referenced by `suppliers[].party`. */
    source: Key,
    /** Goods owner name; defaults to the tenant's only goods owner. */
    goodsOwner: z.string().trim().min(1).optional(),
    /** The document lists every article of this source: an active article that carries this
     *  source in its attributes (`attributes[source]`) and is not in the document is deactivated,
     *  never deleted (owner 2026-10-30: an article that leaves the import must not stay active). */
    complete: z.boolean().default(false),
    /** Display names for classification dimensions the products carry: { key: name }. */
    dimensions: z.record(z.string().trim().min(1).max(80)).default({}),
    products: z.array(ProductImport).min(1),
});
export const LocationKind = z.enum(["zone", "aisle", "rack", "level", "bin", "staging", "workstation", "mobile", "virtual"]);
export const LocationRole = z.enum(["receiving", "storage", "pick_face", "pack", "ship"]);
export const LocationImport = z.object({
    code: Key,
    kind: LocationKind,
    /** Code of the parent location; parents must come earlier in the list or already exist. */
    parent: Key.optional(),
    roles: z.array(LocationRole).default([]),
    storageClass: z.string().trim().min(1).max(40).optional(),
    /** What the place physically takes: only pallets (racking with beams), only loose goods/cartons (shelves), or any. Inherited when absent. */
    holds: z.enum(["pallets", "loose", "any"]).optional(),
    /** Pallets the place takes: 0 = no limit, n = at most n. Inherited when absent. */
    palletCapacity: z.number().int().min(0).max(999).optional(),
    segregationGroup: z.string().trim().max(40).optional(),
    pickSequence: z.number().int().optional(),
    dynamic: z.boolean().default(true),
    active: z.boolean().default(true),
});
export const LocationsImport = z.object({
    kind: z.literal("locations"),
    site: z.object({ name: z.string().trim().min(1).max(100), gln: z.string().regex(/^\d{13}$/).optional() }),
    locations: z.array(LocationImport).min(1),
});
export const ExpectedReceiptSource = z.enum(["despatch-advice", "erp-po", "erp-ret", "document", "manual"]);
export const ExpectedReceiptLineImport = z.object({
    lineNo: z.number().int().positive(),
    /** Any of these resolves the product; the first that matches wins (sku, then gtin, then supplier article). */
    sku: Key.optional(),
    gtin: z.string().trim().optional(),
    supplierArticle: z.string().trim().max(80).optional(),
    description: z.string().trim().max(200).optional(),
    quantity: z.object({ value: DecimalLike, uom: Uom }),
    lot: z.string().trim().max(20).optional(),
    bestBefore: IsoDate.optional(),
    expiry: IsoDate.optional(),
    sscc: z.string().regex(/^\d{18}$/).optional(),
    note: z.string().trim().max(500).optional(),
    /** The line's ERP warehouse (returns): unset = the head's. */
    erpWarehouse: z.string().trim().min(1).max(40).optional(),
});
export const ExpectedReceiptImport = z.object({
    kind: z.literal("expected-receipt"),
    source: ExpectedReceiptSource,
    /** The document's own identifier (purchase order number, despatch advice id). */
    reference: Key,
    /** The same ERP order imported earlier as another kind under another source (owner 2026-11-04: a sales order whose
     *  credit flag was set later is the same order, now a return). That order is closed as replaced when it has not been
     *  started; it is not a cancellation, so the cancellation guard does not hold it. */
    supersedes: z.object({ orderKind: z.enum(["sales-order"]), source: Key }).optional(),
    /** "purchase": goods from a supplier against a purchase order. "return": a customer's goods against the
     *  ERP's return order (owner 2026-11-03: a credit-flagged sales order with the customer and a warehouse
     *  in the head; lines are what the office expects back, and may be none). */
    receiptKind: z.enum(["purchase", "return"]).default("purchase"),
    /** Supplier party key and the identifier space it belongs to. */
    supplier: z.object({ source: Key, key: Key }).optional(),
    /** The customer (returns). */
    customer: z.object({ source: Key, key: Key }).optional(),
    /** The ERP warehouse the head proposes for the lines (returns). */
    erpWarehouse: z.string().trim().min(1).max(40).optional(),
    /** The office's text lines on the order (returns), in order. */
    notes: z.array(z.string().trim().max(400)).max(40).optional(),
    goodsOwner: z.string().trim().min(1).optional(),
    expectedAt: IsoDate.optional(),
    /** "proposed" for extracted documents (not trusted until scans confirm). */
    status: z.enum(["open", "proposed"]).default("open"),
    /** The site the goods arrive at, by the name the locations import gave it (the integration maps
     *  the ERP's warehouse to it; owner 2026-11-01). Unset: the order shows at every site. */
    site: z.string().trim().min(1).max(120).optional(),
    /** The ERP's lock on the order as of this read (owner 2026-11-03): set it, or null to clear; absent = leave as is. */
    lock: z.object({ process: z.string().trim().min(1).max(80), by: z.string().trim().max(120).nullable().optional(), since: z.string().max(40).nullable().optional() }).nullable().optional(),
    external: z.record(z.unknown()).default({}),
    /** A return may come without lines (the office expects "something"); a purchase order needs at least one. */
    lines: z.array(ExpectedReceiptLineImport),
});
/** A customer order to pick (owner 2026-10-28). The ERP integration decides which orders it
 *  sends as pickable; lines are in the stock unit (a 1 057 m carton is ordered as 1 057 m). */
export const SalesOrderLineImport = z.object({
    lineNo: z.number().int().positive(),
    sku: Key.optional(),
    gtin: z.string().trim().optional(),
    description: z.string().trim().max(200).optional(),
    quantity: z.object({ value: DecimalLike, uom: Uom }),
    note: z.string().trim().max(500).optional(),
});
/** A postal address as the ERP printed it on the order: a name, street lines, postcode and city, country. */
export const OrderAddress = z.object({
    name: z.string().trim().max(120).optional(),
    lines: z.array(z.string().trim().max(120)).max(6).default([]),
    postcode: z.string().trim().max(20).optional(),
    city: z.string().trim().max(80).optional(),
    country: z.string().trim().max(60).optional(),
});
/** Head fields the delivery note and the office need, in Trail's names — the integration maps
 *  the ERP's columns onto them (owner 2026-10-31: addresses, references, customer order no,
 *  goods mark and terms come from the ERP). Every field is optional; what the ERP lacks stays out. */
export const SalesOrderDetails = z.object({
    customerOrderNo: z.string().trim().max(80).optional(),
    yourReference: z.string().trim().max(120).optional(),
    ourReference: z.string().trim().max(120).optional(),
    goodsMark: z.string().trim().max(200).optional(),
    deliveryAddress: OrderAddress.optional(),
    invoiceAddress: OrderAddress.optional(),
    /** The delivery method's text as the ERP names it; `carrier` is the pick queue's name. */
    deliveryMethod: z.string().trim().max(80).optional(),
    terms: z.object({ payment: z.string().trim().max(80).optional(), delivery: z.string().trim().max(80).optional() }).optional(),
});
export const SalesOrderImport = z.object({
    kind: z.literal("sales-order"),
    source: Key,
    /** The order number as the customer and the ERP know it. */
    reference: Key,
    customer: z.object({ source: Key, key: Key }).optional(),
    goodsOwner: z.string().trim().min(1).optional(),
    /** The confirmed delivery date; what makes an order pickable is the integration's rule. */
    deliveryDate: IsoDate.optional(),
    /** Pick queue: the carrier that collects, and when — earlier collections are picked first. */
    carrier: z.string().trim().max(80).optional(),
    pickupAt: z.string().datetime({ offset: true }).optional(),
    /** The customer's minimum remaining shelf life at delivery, when they have one. */
    minShelfLifeDays: z.number().int().nonnegative().optional(),
    status: z.enum(["open"]).default("open"),
    /** The site that picks it, by name (the ERP's warehouse mapped by the integration); unset = every site. */
    site: z.string().trim().min(1).max(120).optional(),
    /** Addresses, references and terms for the delivery note (migration 0040). */
    details: SalesOrderDetails.default({}),
    /** The ERP's lock on the order as of this read (owner 2026-11-03): set it, or null to clear; absent = leave as is. */
    lock: z.object({ process: z.string().trim().min(1).max(80), by: z.string().trim().max(120).nullable().optional(), since: z.string().max(40).nullable().optional() }).nullable().optional(),
    external: z.record(z.unknown()).default({}),
    lines: z.array(SalesOrderLineImport).min(1),
});
/** An item reference under the own GS1 prefix that is taken outside Trail's registry: an
 *  article the WMS does not manage, a number registered in the ERP without an article, or a
 *  legacy code. The register never allocates it (GS1: a GTIN is not reused). */
export const GtinReservationImport = z.object({
    gtin: z.string().trim(),
    /** The ERP's article number when the number is on one. */
    sku: Key.optional(),
    note: z.string().trim().max(200).optional(),
});
export const GtinReservationsImport = z.object({
    kind: z.literal("gtin-reservations"),
    source: Key,
    reservations: z.array(GtinReservationImport).min(1),
});
/** One line of an ERP stock snapshot: what the ERP says it holds of a product and lot. */
export const StockBalanceLine = z.object({
    sku: Key.optional(),
    gtin: z.string().trim().optional(),
    /** The ERP's lot; null or absent for stock without batch handling. */
    lot: z.object({ number: z.string().trim().min(1).max(20), bestBefore: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional() }).nullable().optional(),
    quantity: z.object({ value: z.union([z.number(), z.string()]).transform(String), uom: z.string().min(1) }),
    /** The ERP's location text when it keeps one per lot; placed there when it is a Trail location code, else on Oplacerat. */
    location: z.string().trim().max(64).optional(),
    /** This line is one physical container (overrides the document's lotRowsAreContainers). */
    container: z.boolean().optional(),
    /** The ERP's own identifier of this stock row (Visma: the Shipment row number). For a bulk product a row
     *  without a batch number is still one row in Trail, keyed "#<rowKey>" as a technical lot (owner 2026-11-03:
     *  one batch row per receipt, the number never shown to the operator). */
    rowKey: z.string().trim().min(1).max(40).optional(),
    /** When the ERP says this row was last counted (owner 2026-11-04: the history from before Trail, for the check that
     *  every batch is counted within the year's period). */
    lastCounted: IsoDate.nullable().optional(),
    external: z.record(z.unknown()).optional(),
});
/** An ERP stock snapshot (owner 2026-10-29: connecting an ERP should make Trail reflect what
 *  the ERP says it holds, minus locations). The server reconciles it against the ledger per
 *  product + lot: a surplus becomes loose stock on the site's Oplacerat location (or on the
 *  ERP's location when that is a Trail location), a deficit is reported. */
export const StockBalanceImport = z.object({
    kind: z.literal("stock-balance"),
    /** Only the batches named here changed (an incremental read); batches left out are not gone. */
    partial: z.boolean().default(false),
    source: Key,
    goodsOwner: z.string().trim().min(1).max(200).optional(),
    /** The site whose stock this is; the tenant's only own site when absent. */
    site: z.string().trim().min(1).max(100).optional(),
    takenAt: z.string().datetime({ offset: true }),
    /** Owner 2026-10-30: at Kalundgren a batch row with a batch number is always one carton with
     *  an identity (the supplier's GTIN + lot on its label). With this flag such rows become
     *  trade-item containers scannable by that label; rows without a batch number stay loose. */
    lotRowsAreContainers: z.boolean().default(false),
    balances: z.array(StockBalanceLine).min(1),
});
/** The set of orders the ERP currently holds open for one source: an order of that source that is
 *  open in Trail but missing here was finished, removed or taken out of the rule in the ERP, and is
 *  cancelled in Trail (an order being picked is reported, not cancelled). General across ERPs:
 *  incremental reads see changes, never disappearances, so the open set travels with every run. */
export const OpenOrdersImport = z.object({
    kind: z.literal("open-orders"),
    source: Key,
    orderKind: z.enum(["sales-order", "expected-receipt"]),
    /** The whole open set (a complete read): open orders of this source not listed are cancelled. */
    references: z.array(Key).max(50000).optional(),
    /** Only what left the set (an incremental read saw them change out of the rule): these are cancelled. */
    removed: z.array(Key).max(50000).optional(),
    /** The ERP's own picked/finished per line for orders that changed, in or out of the rule, so Trail
     *  sees a pick the office took back in the ERP (owner 2026-11-02) even for an order it holds as picked. */
    state: z.array(z.object({
        reference: Key,
        inRule: z.boolean().optional(),
        /** Why the order is outside the rule, in the adapter's words (shown on the order in Trail). */
        outsideRule: z.string().max(400).nullable().optional(),
        lines: z.array(z.object({ lineNo: z.number().int(), picked: z.number().nullable().optional(), finished: z.number().nullable().optional() })).max(2000),
        /** The ERP's own per-batch rows for the order (Visma: Reservation), as the adapter maps them. */
        batches: z.array(z.record(z.unknown())).max(5000).optional(),
    })).max(5000).optional(),
}); // one of references/removed is required — checked by the importer (a refine would break the discriminated union)
/** Customer-specific article facts (owner 2026-10-30/2026-11-01): the customer's article number and
 *  name, and the texts that go on the customer's carton and pallet labels. The ERP's raw fields
 *  travel under `fields`; the adapter resolves `label` from its own configuration (Kalundgren:
 *  Upplysning 3 = carton label, Upplysning 4 = pallet label). Keyed by customer + product. */
/** The ERP's warehouses (owner 2026-11-03: a warehouse is a ledger bucket, a Trail site a place): the
 *  adapter sends number and name every run; the office says which zones belong to each warehouse in
 *  Lagerplatser. Trail mirrors and picks only warehouses a zone points at. */
export const ErpWarehousesImport = z.object({
    kind: z.literal("erp-warehouses"),
    source: Key,
    warehouses: z.array(z.object({ key: Key, name: z.string().trim().min(1).max(120) })).max(500),
});
export const CustomerProductsImport = z.object({
    kind: z.literal("customer-products"),
    source: Key,
    rows: z.array(z.object({
        customer: z.object({ source: Key, key: Key }),
        sku: z.string().trim().min(1).max(64),
        customerArticle: z.string().trim().max(64).nullable().default(null),
        customerName: z.string().trim().max(200).nullable().default(null),
        fields: z.record(z.string(), z.unknown()).default({}),
        label: z.object({
            cartonText: z.string().max(400).nullable().default(null),
            palletText: z.string().max(400).nullable().default(null),
            /** The customer wants its own label on every carton / pallet. */
            carton: z.boolean().default(false),
            pallet: z.boolean().default(false),
        }).default({}),
    })).max(20_000),
    /** When true, links of this source not in the document are removed. */
    complete: z.boolean().default(false),
});
/** The ERP's locks on orders (owner 2026-11-03: Visma's ChPrc, "LockedByProcessNo" — a row someone is
 *  editing takes no other write; "employees forget they are editing an order and the warehouse tries to
 *  pick it"). The adapter sends every locked open order of its source each run; an order not listed is
 *  free. Trail refuses to start a pick or a receipt on a locked order and holds its reports. */
export const ErpLocksImport = z.object({
    kind: z.literal("erp-locks"),
    source: Key,
    /** Every source whose orders this list speaks for (owner 2026-11-04: the adapter imports sales orders, purchase
     *  receipts and customer returns under separate sources; a lock must reach the order wherever it was imported).
     *  An open order of these sources missing from `locks` is free. Absent: only `source`. */
    sources: z.array(Key).max(20).optional(),
    locks: z.array(z.object({
        orderKind: z.enum(["sales-order", "expected-receipt"]),
        reference: Key,
        /** The source the order was imported under; absent: the document's `source`. */
        source: Key.optional(),
        /** The ERP's own handle for the lock (Visma: the process number), and who/since when, where known. */
        process: z.string().trim().min(1).max(80),
        by: z.string().trim().max(120).nullable().optional(),
        since: z.string().max(40).nullable().optional(),
    })).max(5000),
});
/** What an order carries while the ERP holds it (stored on the order; null = free). */
export const ErpLock = z.object({ process: z.string(), by: z.string().nullable().default(null), since: z.string().nullable().default(null), seenAt: z.string() });
/** The ERP's stock journal (owner 2026-11-04: "speglingen skall huvudsakligen baseras på produkttransaktionerna").
 *  One entry per ERP transaction that changed stock; the adapter maps the ERP's types onto Trail's. Trail keeps
 *  every entry (table erp_journal_entry), matches it against what it caused itself, and turns the rest into
 *  ledger events. `corrected` marks an entry the ERP zeroed afterwards (an undo): its effect is to be reversed. */
export const ErpJournalEntry = z.object({
    /** The ERP's own key of the row (Visma: journal + transaction number); stable across reads. */
    key: Key,
    at: IsoDate,
    /** `opening` is written by Trail itself: the position of a batch row at the floor (today's balance minus
     *  the journal after the floor), never by the adapter. */
    /** `hire` and `loan`: goods out on hire or on loan, and back (owner 2026-11-04: rare, but never skipped). */
    type: z.enum(["sale", "purchase", "loss", "transfer", "consume", "produce", "hire", "loan", "other", "opening"]),
    sku: Key,
    lot: z.string().trim().max(20).nullable().optional(),
    /** The ERP's stock-row key (Visma: ShipmentNo) — the row Trail keys its own by. */
    rowKey: z.string().trim().max(40).nullable().optional(),
    /** Signed effect on stock in the article's stock unit; 0 never arrives (such rows are not stock). */
    stockChange: DecimalLike,
    uom: Uom,
    warehouse: z.string().trim().max(40).nullable().optional(),
    otherWarehouse: z.string().trim().max(40).nullable().optional(),
    otherRowKey: z.string().trim().max(40).nullable().optional(),
    order: z.object({ kind: z.enum(["sales-order", "expected-receipt", "transfer", "production", "other"]), reference: Key, lineNo: z.number().int().nullable().optional() }).nullable().optional(),
    party: z.object({ source: Key, key: Key }).nullable().optional(),
    invoice: z.string().trim().max(60).nullable().optional(),
    /** The ERP user who made it (Trail's own writes carry the company's acting user). */
    by: z.string().trim().max(120).nullable().optional(),
    /** No longer used (owner 2026-11-04): Trail's own rows are recognised by the report written as that ERP user,
     *  not by a flag set when the row is read. Kept so older adapters' documents still parse. */
    byIntegration: z.boolean().default(false),
    corrected: z.boolean().default(false),
    external: z.record(z.unknown()).optional(),
});
export const ErpJournalImport = z.object({
    kind: z.literal("erp-journal"),
    source: Key,
    entries: z.array(ErpJournalEntry).max(2000),
    /** The read this batch came from: a backfill from the floor, or the running incremental read. */
    mode: z.enum(["backfill", "incremental"]).default("incremental"),
    /** The date the backfill started from (owner 2026-11-04: five years before the newest transaction). */
    floor: IsoDate.nullable().optional(),
    /** True once the backfill has read the whole journal from the floor: the opening position can be computed. */
    complete: z.boolean().default(false),
});
export const ImportDocument = z.discriminatedUnion("kind", [ErpJournalImport, ErpLocksImport, ErpWarehousesImport, CustomerProductsImport, PartiesImport, ProductsImport, LocationsImport, ExpectedReceiptImport, GtinReservationsImport, SalesOrderImport, StockBalanceImport, OpenOrdersImport]);
//# sourceMappingURL=reference-import.js.map